import { useState, useEffect, useCallback } from "react";
import ContractExpiryPanel from "../components/ContractExpiryPanel";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

const token = () => localStorage.getItem("portalToken") || "";
const loggedInUser = () => { try { return JSON.parse(localStorage.getItem("portalUser") || "null"); } catch { return null; } };

// ─── Status config ────────────────────────────────────────────────────────────
const STATUSES = [
  { value: "Under review",          label: "Under Review",          color: "#f57c00", bg: "#fff3e0" },
  { value: "Short listed",          label: "Short Listed",          color: "#1565c0", bg: "#e3f2fd" },
  { value: "Offer Letter Sent",     label: "Offer Letter Sent",     color: "#6a1b9a", bg: "#f3e5f5" },
  { value: "Offer Letter Accepted", label: "Offer Letter Accepted", color: "#2e7d32", bg: "#e8f5e9" },
  { value: "Placed",                label: "Placed",                color: "#00695c", bg: "#e0f2f1" },
  { value: "Rejected",              label: "Rejected",              color: "#b71c1c", bg: "#ffebee" },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const SCRUB_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const inputStyle = {
  padding: "8px 10px",
  border: "1px solid #ddd",
  borderRadius: 6,
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  fontSize: 12,
  color: "#555",
  gap: 4,
  marginBottom: 10,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(dateStr) {
  if (!dateStr) return "—";
  try { return new Date(dateStr).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return dateStr; }
}

const MONTH_NAMES = ["january","february","march","april","may","june","july","august","september","october","november","december"];

// Offer letters store the start date as free text (e.g. "1 September 2026"),
// but this modal's Contract Start is a real <input type="date">, which needs
// "YYYY-MM-DD" — same parser CandidateProfile.jsx's contract modal already
// uses to auto-fill from the accepted offer letter instead of re-typing it.
function parseFreeTextDateToISO(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (match) {
    const day = parseInt(match[1], 10);
    const monthIndex = MONTH_NAMES.indexOf(match[2].toLowerCase());
    const year = parseInt(match[3], 10);
    if (monthIndex !== -1) {
      return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return "";
}

function applicantName(a) {
  if (a.user) return `${a.user.first_name} ${a.user.last_name}`;
  return `${a.first_name || ""} ${a.last_name || ""}`.trim() || `Applicant #${a.id}`;
}

// ─── POPIA Badge ──────────────────────────────────────────────────────────────
function POPIABadge({ given, onClick }) {
  const isButton = typeof onClick === "function";
  return (
    <span
      onClick={onClick}
      title={
        !isButton
          ? (given ? "POPIA consent recorded" : "POPIA consent not yet recorded")
          : (given ? "POPIA consent recorded — click for details" : "POPIA consent not yet recorded — click to fix")
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        padding: "2px 7px",
        borderRadius: 4,
        background: given ? "#e8f5e9" : "#fff3e0",
        color: given ? "#2e7d32" : "#e65100",
        border: `1px solid ${given ? "#a5d6a7" : "#ffcc80"}`,
        whiteSpace: "nowrap",
        cursor: isButton ? "pointer" : "default",
      }}
    >
      {given ? "✓" : "!"} POPIA
    </span>
  );
}

// ─── POPIA Consent Modal ──────────────────────────────────────────────────────
function POPIAConsentModal({ applicant, onClose, onChanged }) {
  const name = applicantName(applicant);
  const email = applicant.user?.email || applicant.email || "";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [emailSentTo, setEmailSentTo] = useState("");

  const setConsent = async (value) => {
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ popia_consent: value }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not save");
      onChanged(applicant.id, d.applicant?.popia_consent, d.applicant?.popia_consent_at);
      onClose();
    } catch (e) {
      setError(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const emailConsentLink = async () => {
    setSaving(true); setError(""); setEmailSentTo("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/popia-consent/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not send email");
      setEmailSentTo(email);
    } catch (e) {
      setError(e.message || "Could not send email");
    } finally {
      setSaving(false);
    }
  };

  const given = !!applicant.popia_consent;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 440, maxWidth: "100%", padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#6a0080" }}>POPIA Consent — {name}</h3>

        {given ? (
          <>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#2e7d32" }}>
              ✓ Consent recorded{applicant.popia_consent_at ? ` on ${new Date(applicant.popia_consent_at).toLocaleDateString()}` : ""}.
            </p>
            <button onClick={() => setConsent(false)} disabled={saving} style={{ padding: "8px 14px", background: "#fff", color: "#b71c1c", border: "1px solid #ffcdd2", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              {saving ? "Saving…" : "↩ Undo — mark as not given"}
            </button>
          </>
        ) : (
          <>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              This candidate doesn't have recorded POPIA consent yet. Either they already agreed to it (in person, over the phone, or elsewhere) before being added to the system, or they still need to give it.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              <button onClick={() => setConsent(true)} disabled={saving} style={{ padding: "10px 14px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, textAlign: "left" }}>
                ✓ They already gave consent — mark it now
              </button>
              <button onClick={emailConsentLink} disabled={saving || !email} style={{ padding: "10px 14px", background: "#1565c0", color: "#fff", border: "none", borderRadius: 6, cursor: (saving || !email) ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13, textAlign: "left" }}>
                📧 Email them a consent link{email ? ` (${email})` : " — no email on file"}
              </button>
            </div>

            {emailSentTo && (
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "#2e7d32", background: "#e8f5e9", padding: "8px 12px", borderRadius: 6 }}>
                ✓ Consent request emailed to {emailSentTo}.
              </p>
            )}
          </>
        )}

        {error && <p style={{ margin: "10px 0 0", fontSize: 13, color: "#b71c1c" }}>{error}</p>}

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", background: "#fff", fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Drivers License Badge ───────────────────────────────────────────────────
function DriversBadge({ has }) {
  const yes = has === "yes";
  return (
    <span
      title={yes ? "Has driver's license" : "No driver's license"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        padding: "2px 7px",
        borderRadius: 4,
        background: yes ? "#e8f5e9" : "#fafafa",
        color: yes ? "#2e7d32" : "#999",
        border: `1px solid ${yes ? "#a5d6a7" : "#ddd"}`,
        whiteSpace: "nowrap",
      }}
    >
      {yes ? "✓" : "✗"} Drivers License
    </span>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: "#555", bg: "#f5f5f5" };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: s.bg, color: s.color, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

// ─── Rejection Modal ──────────────────────────────────────────────────────────
function RejectionModal({ applicant, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const name = applicantName(applicant);

  const submit = async () => {
    if (!reason.trim()) { setErr("A rejection reason is required."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/review-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ application_review_status: "Rejected", rejection_reason: reason.trim() }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed");
      onConfirm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 440, maxWidth: "100%", padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#b71c1c" }}>Reject Applicant</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#555" }}>
          You are about to reject <strong>{name}</strong>. This cannot be undone without admin intervention. Please provide a mandatory reason.
        </p>
        <label style={labelStyle}>
          Rejection reason <span style={{ color: "red" }}>*</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="e.g. Qualifications do not meet requirements…"
          />
        </label>
        {err && <p style={{ color: "#b71c1c", fontSize: 13, margin: "0 0 12px" }}>{err}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "9px 16px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", background: "#fff", fontSize: 13 }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ flex: 1, padding: "9px 16px", background: "#b71c1c", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            {saving ? "Rejecting…" : "Confirm Rejection"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Crim Check Confirmation Modal ────────────────────────────────────────────
function ComplianceModal({ applicant, onConfirm, onClose }) {
  const name = applicantName(applicant);
  const [crimChecked, setCrimChecked] = useState(false);
  const [docsChecked, setDocsChecked] = useState(false);
  const canConfirm = crimChecked && docsChecked;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 460, padding: 28, boxShadow: "0 8px 32px rgba(0,0,0,0.22)" }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#1565c0" }}>Compliance Confirmation</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          Before short-listing <strong>{name}</strong>, confirm that the following compliance requirements have been completed:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, cursor: "pointer", padding: "12px 14px", border: `2px solid ${crimChecked ? "#1565c0" : "#ddd"}`, borderRadius: 8, background: crimChecked ? "#e3f2fd" : "#fafafa" }}>
            <input type="checkbox" checked={crimChecked} onChange={(e) => setCrimChecked(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} />
            <span>
              <strong>Criminal Check (Crim Check) cleared</strong><br />
              <span style={{ color: "#777", fontSize: 12 }}>The criminal background check has been completed and the applicant has been cleared.</span>
            </span>
          </label>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, cursor: "pointer", padding: "12px 14px", border: `2px solid ${docsChecked ? "#1565c0" : "#ddd"}`, borderRadius: 8, background: docsChecked ? "#e3f2fd" : "#fafafa" }}>
            <input type="checkbox" checked={docsChecked} onChange={(e) => setDocsChecked(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} />
            <span>
              <strong>All required documentation verified</strong><br />
              <span style={{ color: "#777", fontSize: 12 }}>ID / Passport, HPCSA certificate, CV, and all other required documents have been reviewed and verified.</span>
            </span>
          </label>
        </div>

        {!canConfirm && (
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#e65100", background: "#fff3e0", padding: "8px 12px", borderRadius: 6 }}>
            Both requirements must be confirmed before this applicant can be short-listed.
          </p>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "9px 16px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", background: "#fff", fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{ flex: 2, padding: "9px 16px", background: canConfirm ? "#1565c0" : "#bdbdbd", color: "#fff", border: "none", borderRadius: 6, cursor: canConfirm ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13 }}
          >
            Confirm &amp; Short List ✓
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Offer Letter Accepted / Placement Modal ──────────────────────────────────
function PlacementModal({ applicant, onConfirmed, onClose }) {
  const [step, setStep] = useState("form"); // form | preview | sending | done
  const [fleets, setFleets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newFleet, setNewFleet] = useState("");
  const [newProject, setNewProject] = useState("");
  const [addingFleet, setAddingFleet] = useState(false);
  const [addingProject, setAddingProject] = useState(false);
  const [loadingOpts, setLoadingOpts] = useState(true);

  const [fleetNr, setFleetNr] = useState("");
  const [project, setProject] = useState("");
  const [reportsTo, setReportsTo] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [scrubSize, setScrubSize] = useState(applicant.scrub_size || "");
  const [jacketSize, setJacketSize] = useState(applicant.jacket_size || applicant.scrub_size || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Both on by default; untick when loading existing staff who already
  // have scrubs/training, so nothing is emailed to Moblix or the team.
  const [sendPo, setSendPo] = useState(true);
  const [sendTraining, setSendTraining] = useState(true);

  const user = loggedInUser();
  const name = applicantName(applicant);

  useEffect(() => {
    (async () => {
      try {
        const [fRes, pRes, oRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/fleet`, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(`${API_BASE}/api/admin/projects`, { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/offer-letter`, { headers: { Authorization: `Bearer ${token()}` } }),
        ]);
        const fData = await fRes.json(); const pData = await pRes.json();
        setFleets(fData.fleets || []);
        setProjects(pData.projects || []);

        // Carry over the start date AND who they report to from the offer
        // letter the candidate already accepted — same job, same terms, so
        // re-typing them here is just a chance to introduce a mismatch
        // (offer_letters is sorted newest-first by the backend).
        if (oRes.ok) {
          const oData = await oRes.json();
          const latestOfferLetter = (oData.offer_letters || [])[0];
          const commencementDate = latestOfferLetter ? parseFreeTextDateToISO(latestOfferLetter.start_date) : "";
          if (commencementDate) setStartDate((p) => p || commencementDate);
          if (latestOfferLetter?.reporting_to) setReportsTo((p) => p || latestOfferLetter.reporting_to);
        }
      } catch { /* silent */ }
      finally { setLoadingOpts(false); }
    })();
  }, [applicant.id]);

  const addFleet = async () => {
    if (!newFleet.trim()) return;
    setAddingFleet(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/fleet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ fleet_nr: newFleet.trim(), location_name: newFleet.trim() }),
      });
      const d = await res.json();
      // The server rejects this (not an admin, or that fleet already
      // exists) with an {error: ...} body and no "fleet" key — pushing
      // that undefined into the list used to crash the whole page blank
      // when it was rendered, instead of just showing the real error.
      if (!res.ok || !d.fleet) { setErr(d.error || "Could not add that fleet number."); return; }
      setFleets((p) => [...p, d.fleet]);
      setFleetNr(newFleet.trim());
      setNewFleet("");
    } catch { setErr("Could not reach the server to add that fleet number."); }
    finally { setAddingFleet(false); }
  };

  const addProject = async () => {
    if (!newProject.trim()) return;
    setAddingProject(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ project_name: newProject.trim() }),
      });
      const d = await res.json();
      // Same crash-prevention as addFleet above: the server 403s if you're
      // not an admin, or 409s if that project name already exists (names
      // are uppercased server-side, so "test" and "TEST" collide) — either
      // way there's no "project" key in the response, and rendering an
      // undefined entry in the dropdown used to blank the whole page.
      if (!res.ok || !d.project) { setErr(d.error || "Could not add that project."); return; }
      setProjects((p) => [...p, d.project]);
      // The server uppercases project_name, so select its actual saved
      // value rather than what was typed — otherwise the dropdown shows no
      // selection at all since "test" doesn't match the "TEST" option.
      setProject(d.project.project_name);
      setNewProject("");
    } catch { setErr("Could not reach the server to add that project."); }
    finally { setAddingProject(false); }
  };

  const goPreview = () => {
    if (!fleetNr) { setErr("Please select a Fleet Number."); return; }
    if (!project) { setErr("Please select a Project."); return; }
    if (!startDate || !endDate) { setErr("Please enter start and end dates."); return; }
    if (sendPo && !scrubSize) { setErr("Please select a scrub size."); return; }
    setErr(""); setStep("preview");
  };

  const confirmAndSend = async () => {
    setSaving(true); setStep("sending");
    try {
      await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/placement`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          // manager_name used to default to the logged-in staff member's
          // own name/email (whoever was filling in this form) — that was
          // never actually the candidate's reporting manager, just an
          // unfinished placeholder. Now uses the real "reports to" captured
          // on the offer letter the candidate accepted.
          fleet_nr: fleetNr, project, area: fleetNr,
          manager_name: reportsTo || "", manager_contact: "",
          jacket_size: jacketSize,
          contract_start: startDate, contract_end: endDate,
        }),
      });
      await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ scrub_size: scrubSize, jacket_size: jacketSize }),
      });
      if (sendPo || sendTraining) {
        await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/order-scrubs`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ send_po: sendPo, send_training: sendTraining }),
        });
      }

      // None of the three calls above actually persist a status change —
      // this used to only update the status in the local on-screen list
      // (StatusChanger's onConfirmed), which looked like it worked but
      // reverted on the next refresh since nothing was ever saved. Confirming
      // placement details now moves the candidate straight to "Placed",
      // which also triggers the backend's Ecosystem handoff for that status.
      const statusRes = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/review-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ application_review_status: "Placed" }),
      });
      if (!statusRes.ok) {
        const d = await statusRes.json().catch(() => ({}));
        throw new Error(d.error || "Placement details saved, but could not update the status to Placed.");
      }

      setStep("done");
      if (onConfirmed) onConfirmed();
    } catch (e) { setErr(e.message); setStep("preview"); }
    finally { setSaving(false); }
  };

  const selectedFleet = fleets.find((f) => f.fleet_nr === fleetNr);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 580, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #eee", background: step === "done" ? "#2e7d32" : "#1976d2", borderRadius: "12px 12px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, color: "#fff", fontSize: 15 }}>
            {step === "done" ? (sendPo ? "✓ Purchase Order Sent" : "✓ Placed") : step === "preview" ? (sendPo ? "Review Purchase Order" : "Review Placement") : "Offer Letter Accepted — Placement Details"}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {step === "done" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ margin: "0 0 8px", color: "#2e7d32" }}>{sendPo ? "Purchase Order Sent!" : "Placed!"}</h3>
              <p style={{ color: "#555", fontSize: 14 }}>
                {sendPo ? <>The purchase order has been emailed to <strong>claudia@moblix.co.za</strong>. </> : <>No purchase order was sent. </>}
                {sendTraining ? <>A training request went to Nceba &amp; Rinkie. </> : <>No training request was sent. </>}
                {name}'s status is now <strong>Placed</strong>.
              </p>
              <button onClick={onClose} style={{ marginTop: 16, padding: "10px 24px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
            </div>
          )}

          {step === "sending" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <p style={{ color: "#555" }}>{sendPo ? "Sending purchase order…" : "Saving placement…"}</p>
            </div>
          )}

          {step === "preview" && (
            <div>
              <p style={{ fontSize: 13, color: "#555", margin: "0 0 16px" }}>
                {sendPo
                  ? <>Review the purchase order below. Once confirmed it will be emailed to <strong>claudia@moblix.co.za</strong>.</>
                  : <><strong>No purchase order will be sent.</strong> Review the placement details below.</>}
                {" "}{sendTraining ? "A training request will go to Nceba & Rinkie." : "No training request will be sent."}
              </p>
              <div style={{ border: "2px solid #1976d2", borderRadius: 8, padding: 16, marginBottom: 16, background: "#f8fbff" }}>
                <h4 style={{ margin: "0 0 12px", color: "#1976d2", borderBottom: "1px solid #bbdefb", paddingBottom: 8 }}>{sendPo ? "PURCHASE ORDER" : "PLACEMENT"} — Guud Vision</h4>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <tbody>
                    {[
                      ["Candidate Name", name],
                      ["Position", applicant.position_applied || "—"],
                      ["Fleet / Mobile", fleetNr],
                      ["Location", selectedFleet?.location_name || fleetNr],
                      ["Project", project],
                      ["Reports To", reportsTo || "—"],
                      ["Contract Start", fmt(startDate)],
                      ["Contract End", fmt(endDate)],
                      ["Crim Check", applicant.imei_valid_to ? "Cleared" : "Not Done"],
                      ["Delivery", `Courier to ${selectedFleet?.location_name || fleetNr}`],
                      ["Logged by", user?.name || user?.email || "Admin"],
                    ].map(([l, v]) => (
                      <tr key={l}>
                        <td style={{ padding: "5px 8px", fontWeight: 600, color: "#555", width: "40%", borderBottom: "1px solid #e3f2fd" }}>{l}</td>
                        <td style={{ padding: "5px 8px", borderBottom: "1px solid #e3f2fd" }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sendPo && <>
                <h5 style={{ margin: "14px 0 8px", color: "#1976d2" }}>Order Items</h5>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <thead><tr style={{ background: "#e3f2fd" }}><th style={{ padding: "6px 8px", textAlign: "left" }}>Item</th><th style={{ padding: "6px 8px", textAlign: "left" }}>Detail</th><th style={{ padding: "6px 8px", textAlign: "center" }}>Qty</th></tr></thead>
                  <tbody>
                    {[
                      ["Scrub Set", `Size: ${scrubSize}`, "1"],
                      ["Jacket", `Size: ${jacketSize}`, "1"],
                      ["Name Badge", name, "1"],
                      ["Patches", "Guud Vision patches", "1 set"],
                    ].map(([item, desc, qty]) => (
                      <tr key={item} style={{ borderBottom: "1px solid #e3f2fd" }}>
                        <td style={{ padding: "5px 8px", fontWeight: 600 }}>{item}</td>
                        <td style={{ padding: "5px 8px" }}>{desc}</td>
                        <td style={{ padding: "5px 8px", textAlign: "center" }}>{qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>}
              </div>
              {err && <p style={{ color: "#b3261e", margin: "0 0 12px", fontSize: 13 }}>{err}</p>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setStep("form")} style={{ padding: "10px 16px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", background: "#fff" }}>← Back</button>
                <button onClick={confirmAndSend} disabled={saving} style={{ flex: 1, padding: "10px 24px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                  {sendPo ? <>✓ Confirm &amp; Send Purchase Order</> : <>✓ Confirm Placement</>}
                </button>
              </div>
            </div>
          )}

          {step === "form" && (
            <div>
              {loadingOpts ? <p style={{ color: "#888", fontSize: 13 }}>Loading options…</p> : (
                <>
                  <p style={{ margin: "0 0 16px", fontSize: 13, color: "#555" }}>
                    Complete placement details for <strong>{name}</strong>.
                  </p>

                  {/* Fleet */}
                  <label style={labelStyle}>Fleet Number <span style={{ color: "red" }}>*</span>
                    <select value={fleetNr} onChange={(e) => setFleetNr(e.target.value)} style={inputStyle}>
                      <option value="">— Select Fleet —</option>
                      {fleets.map((f) => <option key={f.fleet_nr} value={f.fleet_nr}>{f.location_name ? `${f.location_name} — ${f.fleet_nr}` : f.fleet_nr}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <input value={newFleet} onChange={(e) => setNewFleet(e.target.value)} placeholder="Add new fleet (e.g. FLT110)" style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={addFleet} disabled={addingFleet || !newFleet.trim()} style={{ padding: "7px 12px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>
                        {addingFleet ? "…" : "+ Add"}
                      </button>
                    </div>
                  </label>

                  {/* Project */}
                  <label style={labelStyle}>Project <span style={{ color: "red" }}>*</span>
                    <select value={project} onChange={(e) => setProject(e.target.value)} style={inputStyle}>
                      <option value="">— Select Project —</option>
                      {projects.map((p) => <option key={p.project_name} value={p.project_name}>{p.project_name}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <input value={newProject} onChange={(e) => setNewProject(e.target.value)} placeholder="Add new project (e.g. DOD)" style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={addProject} disabled={addingProject || !newProject.trim()} style={{ padding: "7px 12px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>
                        {addingProject ? "…" : "+ Add"}
                      </button>
                    </div>
                  </label>

                  {/* Reports to */}
                  <label style={labelStyle}>Reports To
                    <input value={reportsTo} onChange={(e) => setReportsTo(e.target.value)} placeholder="e.g. Dr. Smith" style={inputStyle} />
                    {reportsTo && <span style={{ fontSize: 11, color: "#888" }}>Pre-filled from the offer letter — change it if it's wrong.</span>}
                  </label>

                  {/* Dates */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <label style={labelStyle}>Contract Start <span style={{ color: "red" }}>*</span>
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
                      {startDate && <span style={{ fontSize: 11, color: "#888" }}>Pre-filled from the offer letter — change it if it's wrong.</span>}
                    </label>
                    <label style={labelStyle}>Contract End <span style={{ color: "red" }}>*</span>
                      <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
                    </label>
                  </div>

                  {/* Sizes */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <label style={labelStyle}>Scrub Size <span style={{ color: "red" }}>*</span>
                      <select value={scrubSize} onChange={(e) => { setScrubSize(e.target.value); setJacketSize(e.target.value); }} style={inputStyle}>
                        <option value="">— Select —</option>
                        {SCRUB_SIZES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      <span style={{ fontSize: 11, color: "#888" }}>Jacket will match unless changed</span>
                    </label>
                    <label style={labelStyle}>Jacket Size <span style={{ color: "red" }}>*</span>
                      <select value={jacketSize} onChange={(e) => setJacketSize(e.target.value)} style={inputStyle}>
                        <option value="">— Select —</option>
                        {SCRUB_SIZES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </label>
                  </div>

                  <div style={{ margin: "14px 0 4px", padding: "10px 12px", background: "#f5f7fa", borderRadius: 6, fontSize: 13 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 6 }}>
                      <input type="checkbox" checked={sendPo} onChange={(e) => setSendPo(e.target.checked)} />
                      Send purchase order to Moblix (claudia@moblix.co.za)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={sendTraining} onChange={(e) => setSendTraining(e.target.checked)} />
                      Send training request to Nceba &amp; Rinkie
                    </label>
                  </div>

                  {err && <p style={{ color: "#b3261e", fontSize: 13, margin: "0 0 12px" }}>{err}</p>}
                  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                    <button onClick={onClose} style={{ padding: "10px 16px", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", background: "#fff" }}>Cancel</button>
                    <button onClick={goPreview} style={{ flex: 1, padding: "10px 24px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                      {sendPo ? "Preview Purchase Order →" : "Review Placement →"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Status Change Dropdown ───────────────────────────────────────────────────
function StatusChanger({ applicant, userRole, onChanged }) {
  const [showCompliance, setShowCompliance] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showPlacement, setShowPlacement] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = applicant.application_review_status || "Registered";

  // Options available per role — matches the backend's own valid-transition
  // lists (routes/admin.py: valid_all / valid_viewer). "Offer Letter Sent"
  // and "Offer Letter Accepted" were missing here even though the handling
  // code for picking them (changeStatus below) already existed — someone
  // at "Offer Letter Sent" had no way to actually move to "Offer Letter
  // Accepted" from this dropdown.
  const adminOptions = ["Registered", "Compliance", "Short listed", "Offer Letter Sent", "Offer Letter Accepted", "Rejected"];
  const viewerOptions = ["Compliance", "Short listed", "Offer Letter Sent", "Offer Letter Accepted", "Rejected"];
  const options = STATUSES.filter((s) =>
    (userRole === "admin" ? adminOptions : viewerOptions).includes(s.value)
  );

  const changeStatus = async (newStatus) => {
    // Handle special flows
    if (newStatus === "Rejected") { setShowReject(true); return; }
    if (newStatus === "Short listed") { setShowCompliance(true); return; }
    if (newStatus === "Offer Letter Accepted") { setShowPlacement(true); return; }

    // Direct status change (Offer Letter Sent)
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/review-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ application_review_status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed");
      onChanged(applicant.id, newStatus);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const doStatusSave = async (newStatus) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/review-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ application_review_status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed");
      onChanged(applicant.id, newStatus);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <select
        value={current}
        onChange={(e) => changeStatus(e.target.value)}
        disabled={saving || current === "Placed"}
        style={{
          padding: "5px 8px",
          border: "1px solid #ddd",
          borderRadius: 6,
          fontSize: 12,
          cursor: current === "Placed" ? "default" : "pointer",
          background: "#fff",
          fontFamily: "inherit",
          minWidth: 160,
        }}
      >
        <option value={current} disabled>{STATUS_MAP[current]?.label || current}</option>
        {options.filter((o) => o.value !== current).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {showCompliance && (
        <ComplianceModal
          applicant={applicant}
          onConfirm={() => { setShowCompliance(false); doStatusSave("Short listed"); }}
          onClose={() => { setShowCompliance(false); }}
        />
      )}
      {showReject && (
        <RejectionModal
          applicant={applicant}
          onConfirm={() => { setShowReject(false); onChanged(applicant.id, "Rejected"); }}
          onClose={() => setShowReject(false)}
        />
      )}
      {showPlacement && (
        <PlacementModal
          applicant={applicant}
          onConfirmed={() => { setShowPlacement(false); onChanged(applicant.id, "Placed"); }}
          onClose={() => setShowPlacement(false)}
        />
      )}
    </>
  );
}

// ─── Applicant Row (Under Review table) ───────────────────────────────────────
function ApplicantRow({ applicant, userRole, onChanged, onViewProfile, onOpenPopia }) {
  const name = applicantName(applicant);
  const popiaGiven = applicant.popia_consent || false;

  return (
    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
      <td style={{ padding: "10px 12px", fontSize: 13 }}>
        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{name}</div>
        <div style={{ fontSize: 11, color: "#888" }}>{applicant.user?.email || applicant.email || ""}</div>
      </td>
      <td style={{ padding: "10px 12px", fontSize: 13, color: "#555" }}>{applicant.position_applied || "—"}</td>
      <td style={{ padding: "10px 12px", fontSize: 13, color: "#555" }}>{applicant.province || "—"}</td>
      <td style={{ padding: "10px 12px" }}><POPIABadge given={popiaGiven} onClick={() => onOpenPopia(applicant)} /></td>
      <td style={{ padding: "10px 12px" }}><DriversBadge has={applicant.has_drivers_license} /></td>
      <td style={{ padding: "10px 12px" }}>
        <StatusChanger applicant={applicant} userRole={userRole} onChanged={onChanged} />
      </td>
      <td style={{ padding: "10px 12px" }}>
        <button onClick={() => onViewProfile(applicant.id)} style={{ padding: "5px 10px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12 }}>
          View Profile
        </button>
      </td>
    </tr>
  );
}

// ─── Short Listed Card ────────────────────────────────────────────────────────
function ShortListedCard({ applicant, userRole, onChanged, onViewProfile, onOpenPopia }) {
  const name = applicantName(applicant);
  const popiaGiven = applicant.popia_consent || false;

  const downloadFolder = async () => {
    const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/download-folder`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}_folder.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      alert('No documents available to download.');
    }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e3f2fd", borderRadius: 10, padding: 14, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>{name}</div>
          <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>{applicant.position_applied || "—"} · {applicant.phone || "—"}</div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{applicant.user?.email || ""}</div>
          <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <POPIABadge given={popiaGiven} onClick={() => onOpenPopia(applicant)} />
            <DriversBadge has={applicant.has_drivers_license} />
            {applicant.employment_type === 'locum' && (
              <span style={{ fontSize: 10, fontWeight: 700, background: "#e0f7fa", color: "#006064", padding: "2px 7px", borderRadius: 4, border: "1px solid #80deea" }}>LOCUM</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusChanger applicant={applicant} userRole={userRole} onChanged={onChanged} />
          <button onClick={downloadFolder} style={{ padding: "5px 10px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12 }}>
            ⬇ Download Folder
          </button>
          <button onClick={() => onViewProfile(applicant.id)} style={{ padding: "5px 10px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12 }}>
            View
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Placement Modal ─────────────────────────────────────────────────────

// Whole number of months between two ISO dates, rounded to the nearest
// month and never below 1 — the addendum template states the extension as
// "N months", so a few days either side of a calendar month shouldn't
// produce "0 months" or a fractional count.
function monthsBetweenISO(fromISO, toISO) {
  const from = new Date(`${fromISO}T00:00:00`);
  const to = new Date(`${toISO}T00:00:00`);
  const wholeMonths = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  const dayFraction = (to.getDate() - from.getDate()) / 30;
  return Math.max(1, Math.round(wholeMonths + dayFraction));
}

function addDaysISO(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function EditPlacementModal({ applicant, onClose, onSaved }) {
  const placement = applicant.placement || {};
  const [form, setForm] = useState({
    fleet_nr: placement.fleet_nr || '',
    project: placement.project || '',
    province: applicant.province || '',
    contract_start: placement.contract_start || '',
    contract_end: placement.contract_end || '',
  });
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fleets, setFleets] = useState([]);
  const [latestContract, setLatestContract] = useState(null);
  const [contractChecked, setContractChecked] = useState(false);
  // A contract signed outside TalentPool (paper / imported staff), recorded
  // here so it can be extended — see "Existing contract" below.
  const [existing, setExisting] = useState({
    start: placement.contract_start || '', end: placement.contract_end || '', place: '', file: null,
  });
  const [extendUntil, setExtendUntil] = useState('');
  const [addendumCcEmails, setAddendumCcEmails] = useState(['', '', '']);
  const [addendumNote, setAddendumNote] = useState('');
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const iStyle = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lStyle = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#555', gap: 4, marginBottom: 10 };

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/fleet`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.json())
      .then((d) => setFleets(d.fleets || []))
      .catch(() => {});
    // Needed to know the candidate's on-file contract end date, so
    // "Extend Contract" can generate the addendum against the right
    // starting point rather than guessing from the placement dates alone.
    fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/contract`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.json())
      .then((d) => setLatestContract((d.employment_contracts || [])[0] || null))
      .catch(() => {})
      .finally(() => setContractChecked(true));
  }, [applicant.id]);

  const isExtending = newStatus === 'Placed';
  const needsExisting = isExtending && contractChecked && !latestContract;
  // The on-file contract's end date is the authoritative "current end" to
  // extend from; with no contract in TalentPool, the one being recorded below.
  const currentEndDate = latestContract?.fixed_term_end_date || (needsExisting ? existing.end : placement.contract_end) || '';
  const extendUntilValid = isExtending && extendUntil && (!currentEndDate || extendUntil > currentEndDate);

  // Records the paper contract (+ its signed PDF) so the addendum has
  // something to extend. Returns the new contract record.
  const recordExistingContract = async () => {
    const fd = new FormData();
    fd.append('commencement_date', existing.start);
    fd.append('fixed_term_end_date', existing.end);
    fd.append('primary_place_of_work', existing.place);
    fd.append('project_name', form.project || '');
    if (existing.file) fd.append('file', existing.file);
    const r = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/contract/existing`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` },
      body: fd,
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Could not record the existing contract');
    setLatestContract(d.employment_contract);
    return d.employment_contract;
  };

  const save = async () => {
    if (needsExisting && (!existing.start || !existing.end || !existing.place.trim())) {
      setError('Fill in the existing contract\'s start date, end date and place of work first.');
      return;
    }
    if (needsExisting && existing.end <= existing.start) {
      setError('The existing contract\'s end date must be after its start date.');
      return;
    }
    if (isExtending && !extendUntilValid) {
      setError(currentEndDate
        ? `Please choose a date after the current contract end (${currentEndDate}) to extend until.`
        : 'Please choose a date to extend until.');
      return;
    }
    setSaving(true); setError(''); setAddendumNote('');
    try {
      // Record the paper contract before anything else changes, so a
      // failure here leaves the placement exactly as it was.
      const contract = needsExisting ? await recordExistingContract() : latestContract;

      // Save placement details — when extending, sync the placement's own
      // contract_end to the new date too, so it doesn't fall out of step
      // with what was just emailed to the candidate.
      const placementPayload = isExtending ? { ...form, contract_end: extendUntil } : form;
      const res = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/placement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(placementPayload),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save placement');

      // Update province if changed
      if (form.province !== applicant.province) {
        await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ province: form.province }),
        });
      }

      // Update status if changed
      if (newStatus) {
        const sr = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/review-status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ application_review_status: newStatus }),
        });
        if (!sr.ok) throw new Error((await sr.json()).error || 'Failed to update status');
      }

      // "Extend Contract" only moved a date in the database before — it
      // never actually told the candidate. If the contract end date was
      // pushed out, generate and email the Contract Addendum for signature
      // too, using the candidate's existing contract as the starting point.
      if (isExtending) {
        if (!contract?.fixed_term_end_date) {
          setAddendumNote(
            'Placement saved, but this candidate has no employment contract on file to extend — ' +
            'close this window, open Edit Placement again and fill in the "Existing contract" section.'
          );
        } else {
          const effectiveDate = addDaysISO(contract.fixed_term_end_date, 1);
          const extensionMonths = monthsBetweenISO(effectiveDate, extendUntil);
          try {
            const ar = await fetch(`${API_BASE}/api/admin/applicants/${applicant.id}/contract-addendum`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
              body: JSON.stringify({
                effective_date: effectiveDate,
                extension_months: extensionMonths,
                cc_email: addendumCcEmails.filter((e) => e.trim()).join(', '),
              }),
            });
            const ad = await ar.json();
            setAddendumNote(ar.ok
              ? `Extension document emailed for signature: ${ad.message}`
              : `Placement saved, but the extension email failed: ${ad.error}`);
          } catch (e) {
            setAddendumNote(`Placement saved, but the extension email failed: ${e.message}`);
          }
        }
      }

      if (!isExtending) onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 500, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Edit Placement — {applicantName(applicant)}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={lStyle}>Fleet / Mobile
              <select value={form.fleet_nr} onChange={set('fleet_nr')} style={iStyle}>
                <option value="">— Select —</option>
                {form.fleet_nr && !fleets.some((f) => f.fleet_nr === form.fleet_nr) && (
                  <option value={form.fleet_nr}>{form.fleet_nr} (not in fleet list)</option>
                )}
                {fleets.map((f) => (
                  <option key={f.fleet_nr} value={f.fleet_nr}>
                    {f.location_name ? `${f.location_name} — ${f.fleet_nr}` : f.fleet_nr}
                  </option>
                ))}
              </select>
            </label>
            <label style={lStyle}>Project<input value={form.project} onChange={set('project')} style={iStyle} placeholder="e.g. SIOC" /></label>
            <label style={lStyle}>Province
              <select value={form.province} onChange={set('province')} style={iStyle}>
                <option value="">— Select —</option>
                {['Eastern Cape','Free State','Gauteng','KwaZulu-Natal','Limpopo','Mpumalanga','North West','Northern Cape','Western Cape'].map(p => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label style={lStyle}>Contract Start<input type="date" value={form.contract_start} onChange={set('contract_start')} style={iStyle} /></label>
            <label style={lStyle}>Contract End<input type="date" value={form.contract_end} onChange={set('contract_end')} style={iStyle} /></label>
          </div>

          <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #eee' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Update Status (optional)</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { value: 'Short listed', label: '↩ Back to Short Listed', color: '#1565c0', bg: '#e3f2fd' },
                { value: 'Placed', label: '✓ Extend Contract', color: '#00695c', bg: '#e0f2f1' },
                { value: 'Rejected', label: '✗ End Placement', color: '#b71c1c', bg: '#ffebee' },
              ].map(({ value, label, color, bg }) => (
                <button key={value} onClick={() => { const next = newStatus === value ? '' : value; setNewStatus(next); if (next !== 'Placed') { setExtendUntil(''); setAddendumCcEmails(['', '', '']); } }}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `2px solid ${newStatus === value ? color : '#ddd'}`, background: newStatus === value ? bg : '#fff', color: newStatus === value ? color : '#555', fontFamily: 'inherit' }}>
                  {label}
                </button>
              ))}
            </div>
            {newStatus && newStatus !== 'Placed' && <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Status will be changed to: <strong>{newStatus}</strong></div>}
            {isExtending && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #ddd' }}>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                  Current contract end: <strong>{currentEndDate || 'none on file'}</strong>. Saving will email the
                  candidate a Contract Addendum to review and sign — the project, fleet, etc. don't need to change
                  for this, only the new end date below.
                </div>
                {needsExisting && (
                  <div style={{ margin: '0 0 12px', padding: '10px 12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Existing contract</div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>
                      This candidate's contract isn't in TalentPool yet (signed on paper or before TalentPool).
                      Record it here so the addendum can extend it. Nothing is emailed for this step; the
                      signed PDF is attached to the addendum email as the original contract.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <label style={lStyle}>Contract start<input type="date" value={existing.start} onChange={(e) => setExisting((p) => ({ ...p, start: e.target.value }))} style={iStyle} /></label>
                      <label style={lStyle}>Current end date<input type="date" value={existing.end} onChange={(e) => setExisting((p) => ({ ...p, end: e.target.value }))} style={iStyle} /></label>
                    </div>
                    <label style={lStyle}>Place of work (printed on the addendum)<input type="text" value={existing.place} placeholder="e.g. Kathu" onChange={(e) => setExisting((p) => ({ ...p, place: e.target.value }))} style={iStyle} /></label>
                    <label style={lStyle}>Signed contract (PDF, recommended)
                      <input type="file" accept="application/pdf,.pdf" onChange={(e) => setExisting((p) => ({ ...p, file: e.target.files?.[0] || null }))} style={{ fontSize: 12 }} />
                    </label>
                  </div>
                )}
                <label style={lStyle}>Extend until<input type="date" value={extendUntil} onChange={(e) => setExtendUntil(e.target.value)} style={iStyle} min={currentEndDate || undefined} /></label>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>CC (optional — e.g. HR, the candidate's manager)</div>
                <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                  {addendumCcEmails.map((val, i) => (
                    <input key={i} type="email" value={val} style={iStyle}
                      placeholder={['e.g. hr@guudvision.health', 'e.g. manager@guudvision.health', 'another address (optional)'][i]}
                      onChange={(e) => setAddendumCcEmails((p) => p.map((v, j) => (j === i ? e.target.value : v)))} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && <div style={{ marginTop: 12, padding: '8px 12px', background: '#ffebee', color: '#b71c1c', borderRadius: 6, fontSize: 13 }}>{error}</div>}
          {addendumNote && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: addendumNote.includes('failed') || addendumNote.includes('no employment contract') ? '#fff3e0' : '#e0f2f1', color: addendumNote.includes('failed') || addendumNote.includes('no employment contract') ? '#e65100' : '#00695c', borderRadius: 6, fontSize: 13 }}>
              {addendumNote}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {addendumNote ? (
              <button onClick={onSaved} style={{ flex: 1, padding: '10px 0', background: '#00695c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>Done</button>
            ) : (
              <>
                <button onClick={onClose} style={{ flex: 1, padding: '10px 0', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ flex: 2, padding: '10px 0', background: saving ? '#ccc' : '#00695c', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Placed Card ──────────────────────────────────────────────────────────────
function PlacedCard({ applicant, onViewProfile, onChanged, userRole, onOpenPopia }) {
  const name = applicantName(applicant);
  const placement = applicant.placement || null;
  const popiaGiven = applicant.popia_consent || false;
  const [showEdit, setShowEdit] = useState(false);

  const contractStart = placement?.contract_start || null;
  const contractEnd = placement?.contract_end || null;

  const today = new Date();
  const endDate = contractEnd ? new Date(contractEnd) : null;
  const daysLeft = endDate ? Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)) : null;
  const isExpiringSoon = daysLeft !== null && daysLeft <= 14 && daysLeft >= 0;
  const isExpired = daysLeft !== null && daysLeft < 0;

  // HPCSA/CPD/IMEI compliance checks
  const complianceWarnings = [];
  const complianceChecks = [
    { label: 'HPCSA', to: applicant.hpcsa_valid_to },
    { label: 'CPD', to: applicant.cpd_valid_to },
    { label: 'Crim Check', to: applicant.imei_valid_to },
  ];
  complianceChecks.forEach(({ label, to }) => {
    if (!to) return;
    const expiry = new Date(to);
    const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (days < 0) complianceWarnings.push({ label, status: 'expired' });
    else if (days <= 30) complianceWarnings.push({ label, status: 'expiring', days });
  });

  const borderColor = isExpired ? "#ffcdd2" : isExpiringSoon ? "#ffe0b2" : complianceWarnings.length > 0 ? "#fff3e0" : "#e0f2f1";

  return (
    <>
      {showEdit && <EditPlacementModal applicant={applicant} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); if (onChanged) onChanged(); }} />}
      <div style={{ background: "#fff", border: `1px solid ${borderColor}`, borderRadius: 10, padding: "12px 16px", marginBottom: 10 }}>

        {/* Top row — name, badges, buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>{name}</span>
          <span style={{ fontSize: 12, color: "#777" }}>{applicant.position_applied || ""}</span>
          <StatusBadge status="Placed" />
          <POPIABadge given={popiaGiven} onClick={() => onOpenPopia(applicant)} />
          {isExpired && <span style={{ fontSize: 10, fontWeight: 700, background: "#ffcdd2", color: "#b71c1c", padding: "2px 7px", borderRadius: 4 }}>⚠ CONTRACT ENDED</span>}
          {isExpiringSoon && !isExpired && <span style={{ fontSize: 10, fontWeight: 700, background: "#ffe0b2", color: "#e65100", padding: "2px 7px", borderRadius: 4 }}>ENDING SOON</span>}
          {complianceWarnings.map(({ label, status, days }) => (
            <span key={label} style={{ fontSize: 10, fontWeight: 700, background: status === "expired" ? "#ffcdd2" : "#fff3e0", color: status === "expired" ? "#b71c1c" : "#e65100", padding: "2px 7px", borderRadius: 4 }}>
              {status === "expired" ? `⚠ ${label} EXPIRED` : `⚠ ${label} expires in ${days}d`}
            </span>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button onClick={() => setShowEdit(true)} style={{ padding: "4px 10px", background: "#e3f2fd", color: "#1565c0", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>✏ Edit</button>
            <button onClick={() => onViewProfile(applicant.id)} style={{ padding: "4px 10px", background: "#00695c", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>View Profile</button>
          </div>
        </div>

        {/* Info row — all on one line */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Fleet", value: placement?.fleet_nr },
            { label: "Project", value: placement?.project },
            { label: "Province", value: applicant.province },
            { label: "Start", value: fmt(contractStart) },
            { label: "End", value: fmt(contractEnd) },
            { label: "Days left", value: daysLeft !== null ? (isExpired ? "Ended" : `${daysLeft} days`) : null },
          ].filter(({ value }) => value && value !== "—").map(({ label, value }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, background: "#f8f9fa", borderRadius: 6, padding: "4px 10px", fontSize: 12 }}>
              <span style={{ color: "#999", fontWeight: 600, textTransform: "uppercase", fontSize: 10 }}>{label}</span>
              <span style={{ fontWeight: 600, color: label === "Days left" && isExpired ? "#b71c1c" : label === "Days left" && isExpiringSoon ? "#e65100" : "#1a1a1a" }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ title, count, color = "#1a1a1a", bg = "#f5f5f5" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color }}>{title}</h2>
      <span style={{ fontSize: 12, fontWeight: 700, background: bg, color, padding: "2px 10px", borderRadius: 12, border: `1px solid ${color}33` }}>{count}</span>
    </div>
  );
}

// ─── Add Applicant Form ───────────────────────────────────────────────────────
const PROVINCES = ["Eastern Cape","Free State","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Northern Cape","Western Cape"];
const EMPTY_FORM = {
  first_name:"", last_name:"", email:"", phone:"", address:"", city:"", province:"",
  postal_code:"", date_of_birth:"", gender:"", id_number:"", folder_number:"",
  position_applied:"", hpcsa_number:"", years_experience:"", current_employer:"",
  employment_type:"", hourly_rate:"",
  notice_period:"", salary_expectation:"", availability_status:"available",
  available_from:"", relocation_preferences:"", admin_notes:"", additional_info:"",
  popia_consent: false,
};

function AddApplicantForm({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [success, setSuccess] = useState("");

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setCheck = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const handleIdChange = (e) => {
    const val = e.target.value;
    setForm((p) => {
      const updated = { ...p, id_number: val };
      const digits = val.replace(/\D/g, "");
      if (digits.length >= 6) {
        const yy = parseInt(digits.substring(0, 2), 10);
        const mm = digits.substring(2, 4);
        const dd = digits.substring(4, 6);
        const currentYY = new Date().getFullYear() % 100;
        const year = yy <= currentYY ? 2000 + yy : 1900 + yy;
        updated.date_of_birth = `${year}-${mm}-${dd}`;
      }
      return updated;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setWarning(""); setSuccess("");
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("First and last name are required."); return; }
    if (!form.email.trim() || !form.email.includes("@")) { setError("A valid email is required."); return; }
    if (!form.popia_consent) { setError("Please confirm POPIA consent was obtained from this candidate before adding them."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ...form, years_experience: form.years_experience ? parseInt(form.years_experience) : null, popia_consent_at: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error || "Could not create applicant";
        if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("already") || msg.toLowerCase().includes("id number")) {
          setWarning(msg); return;
        }
        throw new Error(msg);
      }
      setSuccess(`Applicant ${form.first_name} ${form.last_name} created successfully.`);
      setForm(EMPTY_FORM);
      if (onSuccess) onSuccess();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const iStyle = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: "inherit" };
  const lStyle = { display: "flex", flexDirection: "column", fontSize: 12, color: "#555", gap: 4 };
  const g2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 };
  const g3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 };

  return (
    <div style={{ background: "#fff", border: "1px solid #e8d5f5", borderRadius: 10, marginBottom: 20, overflow: "hidden" }}>
      <button onClick={() => setOpen((p) => !p)} style={{ width: "100%", padding: "12px 16px", background: open ? "#f3e5f7" : "#fff", border: "none", borderBottom: open ? "1px solid #e8d5f5" : "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#6a0080" }}>➕ Add Single Applicant</span>
        <span style={{ fontSize: 12, color: "#999" }}>{open ? "▲ Close" : "▼ Open"}</span>
      </button>
      {open && (
        <form onSubmit={submit} style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6a0080", borderBottom: "1px solid #ede7f6", paddingBottom: 6, marginBottom: 10 }}>Account Details</div>
          <div style={g3}>
            <label style={lStyle}>First name *<input value={form.first_name} onChange={set("first_name")} required style={iStyle} /></label>
            <label style={lStyle}>Last name *<input value={form.last_name} onChange={set("last_name")} required style={iStyle} /></label>
            <label style={lStyle}>Email *<input type="email" value={form.email} onChange={set("email")} required style={iStyle} /></label>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#6a0080", borderBottom: "1px solid #ede7f6", paddingBottom: 6, marginBottom: 10 }}>Personal Details</div>
          <div style={g3}>
            <label style={lStyle}>Phone<input value={form.phone} onChange={set("phone")} style={iStyle} /></label>
            <label style={lStyle}>ID / Passport number<input value={form.id_number} onChange={handleIdChange} style={iStyle} /></label>
            <label style={lStyle}>Date of birth<input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} style={iStyle} /></label>
          </div>
          <div style={g2}>
            <label style={lStyle}>Gender<select value={form.gender} onChange={set("gender")} style={iStyle}><option value="">— Select —</option><option>Male</option><option>Female</option></select></label>
            <label style={lStyle}>Folder number<input value={form.folder_number} onChange={set("folder_number")} style={iStyle} /></label>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#6a0080", borderBottom: "1px solid #ede7f6", paddingBottom: 6, marginBottom: 10 }}>Location</div>
          <div style={g3}>
            <label style={lStyle}>Address<input value={form.address} onChange={set("address")} style={iStyle} /></label>
            <label style={lStyle}>City / Suburb<input value={form.city} onChange={set("city")} style={iStyle} /></label>
            <label style={lStyle}>Postal code<input value={form.postal_code} onChange={set("postal_code")} style={iStyle} /></label>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={lStyle}>Province<select value={form.province} onChange={set("province")} style={iStyle}><option value="">— Select —</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></label>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#6a0080", borderBottom: "1px solid #ede7f6", paddingBottom: 6, marginBottom: 10 }}>Professional</div>
          <div style={g3}>
            <label style={lStyle}>Position applied<select value={form.position_applied} onChange={set("position_applied")} style={iStyle}><option value="">— Select —</option><option>Optometrist</option><option>Optical Dispenser</option><option>Other</option></select></label>
            <label style={lStyle}>HPCSA number<input value={form.hpcsa_number} onChange={set("hpcsa_number")} style={iStyle} /></label>
            <label style={lStyle}>Years experience<input type="number" value={form.years_experience} onChange={set("years_experience")} min={0} style={iStyle} /></label>
          </div>
          <div style={g2}>
            <label style={lStyle}>Employment type
              <select value={form.employment_type} onChange={set("employment_type")} style={iStyle}>
                <option value="">— Select —</option>
                <option value="permanent">Permanent Employment</option>
                <option value="fixed_term">Fixed Term</option>
                <option value="locum">Locum</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label style={lStyle}>Current employer<input value={form.current_employer} onChange={set("current_employer")} style={iStyle} placeholder={form.employment_type === 'locum' ? 'Locum / agency' : ''} /></label>
          </div>
          <div style={g3}>
            <label style={lStyle}>Notice period<input value={form.notice_period} onChange={set("notice_period")} style={iStyle} placeholder="e.g. 1 month" /></label>
            {form.employment_type === 'locum' ? (
              <label style={lStyle}>Rate per hour<input value={form.hourly_rate} onChange={set("hourly_rate")} style={iStyle} placeholder="e.g. R250/hr" /></label>
            ) : (
              <label style={lStyle}>Salary expectation<input value={form.salary_expectation} onChange={set("salary_expectation")} style={iStyle} placeholder="e.g. R25 000 pm" /></label>
            )}
          </div>
          <div style={g2}>
            <label style={lStyle}>Available from<input type="date" value={form.available_from} onChange={set("available_from")} style={iStyle} /></label>
            <label style={lStyle}>Relocation preferences<input value={form.relocation_preferences} onChange={set("relocation_preferences")} style={iStyle} /></label>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#6a0080", borderBottom: "1px solid #ede7f6", paddingBottom: 6, marginBottom: 10 }}>Notes</div>
          <label style={{ ...lStyle, marginBottom: 10 }}>Admin notes<textarea value={form.admin_notes} onChange={set("admin_notes")} rows={2} style={{ ...iStyle, resize: "vertical" }} /></label>

          <div style={{ background: "#fdf6ff", border: "1px solid #e8d5f5", borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={form.popia_consent} onChange={setCheck("popia_consent")} style={{ marginTop: 2 }} />
              <span>I confirm this candidate has already given POPIA consent for Guud Vision to store, process and contact them (including by email) about this application, before being added here.</span>
            </label>
          </div>

          {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>{error}</div>}
          {warning && <div style={{ background: "#fff3e0", color: "#e65100", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>⚠️ {warning}</div>}
          {success && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>✓ {success}</div>}

          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={saving} style={{ flex: 1, padding: "10px 0", background: saving ? "#ccc" : "#6a0080", color: "#fff", border: "none", borderRadius: 6, cursor: saving ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 14, fontFamily: "inherit" }}>
              {saving ? "Creating…" : "Create Applicant"}
            </button>
            <button type="button" onClick={() => { setForm(EMPTY_FORM); setError(""); setWarning(""); setSuccess(""); }} style={{ padding: "10px 16px", background: "#fff", color: "#555", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>
              Clear
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── CSV Upload ───────────────────────────────────────────────────────────────
function CsvUpload({ onSuccess, userRole }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [popiaConfirmed, setPopiaConfirmed] = useState(false);

  if (userRole !== "admin") return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a CSV file."); return; }
    if (!popiaConfirmed) { setError("Please confirm POPIA consent was obtained from every candidate in this file."); return; }
    setUploading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("popia_consent_confirmed", "true");
      const res = await fetch(`${API_BASE}/api/admin/applicants/bulk-csv`, {
        method: "POST", headers: { Authorization: `Bearer ${token()}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setResult(data);
      setFile(null);
      setPopiaConfirmed(false);
      if (onSuccess) onSuccess();
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e3f2fd", borderRadius: 10, marginBottom: 20, overflow: "hidden" }}>
      <button onClick={() => setOpen((p) => !p)} style={{ width: "100%", padding: "12px 16px", background: open ? "#e3f2fd" : "#fff", border: "none", borderBottom: open ? "1px solid #e3f2fd" : "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#1565c0" }}>📂 Bulk Import via CSV</span>
        <span style={{ fontSize: 12, color: "#999" }}>{open ? "▲ Close" : "▼ Open"}</span>
      </button>
      {open && (
        <div style={{ padding: 16 }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#555" }}>Upload a CSV file with columns: <code>first_name, last_name, email, phone, province, position_applied, hpcsa_number, id_number</code></p>
          <form onSubmit={submit}>
            <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={popiaConfirmed} onChange={(e) => setPopiaConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                <span>I confirm POPIA consent was already obtained from every candidate in this file (to store, process and contact them, including by email) before uploading them.</span>
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} style={{ fontSize: 13, flex: 1 }} />
              <button type="submit" disabled={uploading || !file || !popiaConfirmed} style={{ padding: "8px 18px", background: (uploading || !popiaConfirmed) ? "#ccc" : "#1565c0", color: "#fff", border: "none", borderRadius: 6, cursor: (uploading || !popiaConfirmed) ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit", whiteSpace: "nowrap" }}>
                {uploading ? "Uploading…" : "Upload CSV"}
              </button>
            </div>
          </form>
          {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginTop: 10 }}>{error}</div>}
          {result && (
            <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 6, padding: "10px 12px", fontSize: 13, marginTop: 10 }}>
              ✓ Import complete — {result.created || 0} created, {result.skipped || 0} skipped, {result.errors || 0} errors.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard({ onNavigateToProfile }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fetchError, setFetchError] = useState("");
  const user = loggedInUser();
  const userRole = user?.role || "viewer";

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/applicants?per_page=500`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();

      // A failed request (expired session, server error, etc.) used to be
      // swallowed silently here, leaving every section on the dashboard
      // looking like a real "0 applicants" — with no indication anything
      // had gone wrong. Surface it instead.
      if (!res.ok) {
        setFetchError(
          res.status === 401 || res.status === 422
            ? "Your session has expired — please log out and log back in to see applicants."
            : (data && data.error) || `Could not load applicants (server returned ${res.status}).`
        );
        setApplicants([]);
        return;
      }

      const list = Array.isArray(data.applicants) ? data.applicants : Array.isArray(data) ? data : [];
      // For placed applicants, also fetch placement details
      const placedIds = list.filter((a) => a.application_review_status === "Placed").map((a) => a.id);

      const placementResults = await Promise.all(
        placedIds.map((id) =>
          fetch(`${API_BASE}/api/admin/applicants/${id}/placement`, { headers: { Authorization: `Bearer ${token()}` } })
            .then((r) => r.json())
            .then((d) => ({ id, placement: d.placement }))
            .catch(() => ({ id, placement: null }))
        )
      );
      const placementMap = Object.fromEntries(placementResults.map(({ id, placement }) => [id, placement]));

      setApplicants(list.map((a) => ({ ...a, placement: placementMap[a.id] || null })));
    } catch (e) {
      setFetchError("Could not reach the server to load applicants. Check your connection and try again.");
      setApplicants([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  const handleStatusChange = (id, newStatus) => {
    setApplicants((prev) =>
      prev.map((a) => a.id === id ? { ...a, application_review_status: newStatus } : a)
    );
  };

  const [popiaModalApplicant, setPopiaModalApplicant] = useState(null);

  const handlePopiaChange = (id, consent, consentAt) => {
    setApplicants((prev) =>
      prev.map((a) => a.id === id ? { ...a, popia_consent: consent, popia_consent_at: consentAt } : a)
    );
  };

  const viewProfile = (id) => {
    if (onNavigateToProfile) onNavigateToProfile(id);
    else window.location.href = `/candidate/${id}`;
  };

  // Filter by search
  const filterApplicant = (a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = applicantName(a).toLowerCase();
    return (
      name.includes(q) ||
      (a.position_applied || "").toLowerCase().includes(q) ||
      (a.province || "").toLowerCase().includes(q) ||
      (a.user?.email || a.email || "").toLowerCase().includes(q) ||
      (a.hpcsa_number || "").toLowerCase().includes(q) ||
      (a.id_number || "").toLowerCase().includes(q)
    );
  };

  // Status buckets
  const byStatus = (status) => applicants.filter((a) => a.application_review_status === status && filterApplicant(a));

  // Registered = new applicants (include legacy "Under review" for backwards compat)
  const registered          = [...byStatus("Registered"), ...byStatus("Under review")];
  const compliance          = byStatus("Compliance");
  const shortListed         = byStatus("Short listed");
  const offerLetterSent     = byStatus("Offer Letter Sent");
  const offerLetterAccepted = byStatus("Offer Letter Accepted");
  const placed              = byStatus("Placed");

  // Group short listed by province
  const shortListedByProvince = shortListed.reduce((acc, a) => {
    const prov = a.province || "Unknown Province";
    if (!acc[prov]) acc[prov] = [];
    acc[prov].push(a);
    return acc;
  }, {});

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading applicants…</div>;

  return (
    <div style={{ padding: "0 0 40px", fontFamily: "system-ui, -apple-system, sans-serif" }}>

      {fetchError && (
        <div style={{ marginBottom: 20, padding: "12px 16px", background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: 8, color: "#7d5200", fontSize: 13.5, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <span style={{ flex: 1 }}>{fetchError}</span>
          <button onClick={fetchApplicants} style={{ padding: "6px 12px", background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5, whiteSpace: "nowrap" }}>
            Retry
          </button>
        </div>
      )}

      {/* ── Contracts expiring, grouped by vehicle, with reminder emails ── */}
      <ContractExpiryPanel onOpenProfile={viewProfile} />

      {/* ── Add applicant + CSV ── */}
      <AddApplicantForm onSuccess={fetchApplicants} />
      <CsvUpload onSuccess={fetchApplicants} userRole={userRole} />

      {/* ── Search bar ── */}
      <div style={{ marginBottom: 28, display: "flex", gap: 12, alignItems: "center" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 16 }}>🔍</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search applicants by name, position, province, email, ID or HPCSA number…"
            style={{ ...inputStyle, paddingLeft: 36, fontSize: 14, background: "#fff", border: "1px solid #ddd", borderRadius: 8 }}
          />
        </div>
        {search && (
          <button onClick={() => setSearch("")} style={{ padding: "8px 14px", border: "1px solid #ddd", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#555" }}>
            Clear
          </button>
        )}
        <button onClick={fetchApplicants} style={{ padding: "8px 14px", border: "1px solid #1976d2", borderRadius: 8, background: "#e3f2fd", color: "#1565c0", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── Registered Applicants ── */}
      {(userRole === "admin" || userRole === "viewer") && (
        <div style={{ marginBottom: 36 }}>
          <SectionHeader title="Registered Applicants" count={registered.length} color="#388e3c" bg="#f1f8e9" />
          {registered.length === 0 ? (
            <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No registered applicants."}</p>
          ) : (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #c8e6c9", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                    {["Applicant", "Position", "Province", "POPIA", "Drivers License", "Status", "Action"].map((h) => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {registered.map((a) => (
                    <ApplicantRow key={a.id} applicant={a} userRole={userRole} onChanged={handleStatusChange} onViewProfile={viewProfile} onOpenPopia={setPopiaModalApplicant} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Compliance ── */}
      {(userRole === "admin" || userRole === "viewer") && (
        <div style={{ marginBottom: 36 }}>
          <SectionHeader title="Compliance Process" count={compliance.length} color="#f57c00" bg="#fff3e0" />
          {compliance.length === 0 ? (
            <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No applicants in compliance."}</p>
          ) : (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #ffe0b2", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                    {["Applicant", "Position", "Province", "POPIA", "Drivers License", "Status", "Action"].map((h) => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compliance.map((a) => (
                    <ApplicantRow key={a.id} applicant={a} userRole={userRole} onChanged={handleStatusChange} onViewProfile={viewProfile} onOpenPopia={setPopiaModalApplicant} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Short Listed — grouped by province ── */}
      <div style={{ marginBottom: 36 }}>
        <SectionHeader title="Short Listed" count={shortListed.length} color="#1565c0" bg="#e3f2fd" />
        {shortListed.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No short listed applicants."}</p>
        ) : (
          Object.entries(shortListedByProvince).sort(([a], [b]) => a.localeCompare(b)).map(([province, list]) => (
            <div key={province} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1565c0" }}>📍 {province}</span>
                <span style={{ fontSize: 11, color: "#888" }}>({list.length})</span>
              </div>
              {list.map((a) => (
                <ShortListedCard key={a.id} applicant={a} userRole={userRole} onChanged={handleStatusChange} onViewProfile={viewProfile} onOpenPopia={setPopiaModalApplicant} />
              ))}
            </div>
          ))
        )}
      </div>

      {/* ── Offer Letter Sent ── */}
      <div style={{ marginBottom: 36 }}>
        <SectionHeader title="Offer Letter Sent" count={offerLetterSent.length} color="#6a1b9a" bg="#f3e5f5" />
        {offerLetterSent.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No applicants at this stage."}</p>
        ) : (
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e1bee7", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                {["Applicant", "Position", "Province", "POPIA", "Status", "Action"].map((h) => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {offerLetterSent.map((a) => (
                  <ApplicantRow key={a.id} applicant={a} userRole={userRole} onChanged={handleStatusChange} onViewProfile={viewProfile} onOpenPopia={setPopiaModalApplicant} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Offer Letter Accepted ── */}
      <div style={{ marginBottom: 36 }}>
        <SectionHeader title="Offer Letter Accepted" count={offerLetterAccepted.length} color="#2e7d32" bg="#e8f5e9" />
        {offerLetterAccepted.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No applicants at this stage."}</p>
        ) : (
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #c8e6c9", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                {["Applicant", "Position", "Province", "POPIA", "Status", "Action"].map((h) => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {offerLetterAccepted.map((a) => (
                  <ApplicantRow key={a.id} applicant={a} userRole={userRole} onChanged={handleStatusChange} onViewProfile={viewProfile} onOpenPopia={setPopiaModalApplicant} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Placed ── */}
      <div style={{ marginBottom: 36 }}>
        <SectionHeader title="Placed" count={placed.length} color="#00695c" bg="#e0f2f1" />
        {placed.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: 13 }}>{search ? "No results match your search." : "No placed applicants."}</p>
        ) : (
          placed.map((a) => (
            <PlacedCard key={a.id} applicant={a} onViewProfile={viewProfile} onChanged={fetchApplicants} userRole={userRole} onOpenPopia={setPopiaModalApplicant} />
          ))
        )}
      </div>

      {popiaModalApplicant && (
        <POPIAConsentModal
          applicant={popiaModalApplicant}
          onClose={() => setPopiaModalApplicant(null)}
          onChanged={handlePopiaChange}
        />
      )}
    </div>
  );
}
